import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { PartyPopper, Loader2, Calculator } from "lucide-react";
import { LineReveal, Reveal } from "@/components/Reveal";
import { submitBooking } from "@/lib/api";

const EVENT_TYPES = [
  "Office Party", "Employee Appreciation", "School Event", "Church Event",
  "Community Event", "Fundraiser", "Special Celebration", "Wedding", "Other",
];

const MENU_OPTIONS = [
  { name: "Cinnamon Rolls Bar", rate: 4 },
  { name: "Biscuits & Gravy Bar", rate: 6 },
  { name: "Coffee & Latte Bar", rate: 3 },
  { name: "Full Crazy Package", rate: 10 },
];

const BASE_FEE = 150;
const INITIAL = {
  name: "", email: "", phone: "", event_date: "", event_time: "",
  guests: 75, location: "", event_type: EVENT_TYPES[0], menu_choice: MENU_OPTIONS[3].name, notes: "",
};

export default function Book() {
  const [form, setForm] = useState(INITIAL);
  const [done, setDone] = useState(false);

  const estimate = useMemo(() => {
    const opt = MENU_OPTIONS.find((o) => o.name === form.menu_choice) || MENU_OPTIONS[3];
    return BASE_FEE + form.guests * opt.rate;
  }, [form.guests, form.menu_choice]);

  const mutation = useMutation({
    mutationFn: (payload) => submitBooking(payload),
    onSuccess: () => {
      setDone(true);
      toast.success("Booking request sent! Check your inbox for a confirmation.", { duration: 6000 });
    },
    onError: () => {
      toast.error("Whoops — something went wrong sending that. Please try again!");
    },
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    mutation.mutate({ ...form, guests: Number(form.guests), estimate });
  };

  return (
    <div data-testid="book-page">
      <section className="bg-cb-teal border-b-3 border-cb-black py-16 sm:py-24 relative overflow-hidden">
        <div className="hidden md:block absolute -bottom-20 -right-16 w-72 h-72 bg-cb-pink border-3 border-cb-black opacity-70 animate-spin-slow" style={{ borderRadius: "45% 55% 60% 40% / 55% 45% 55% 45%" }} />
        <div className="max-w-7xl mx-auto px-4 text-center relative">
          <span className="cb-badge bg-cb-yellow text-cb-black mb-6 inline-block" data-testid="book-badge">We Bring The Party</span>
          <h1 className="font-display font-bold uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="book-headline">
            <LineReveal
              lines={[
                <span key="a" className="text-outline-white inline-block -rotate-1">Book Crazy Buns</span>,
                <span key="b" className="text-outline-yellow inline-block rotate-1">For Your Event</span>,
              ]}
            />
          </h1>
          <Reveal delay={0.55}>
            <p className="mt-5 font-bold text-cb-black/70 text-base md:text-lg max-w-xl mx-auto">
              Tell us about your event and get an instant ballpark estimate. We'll reply within 24 hours to lock it in.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cb-cream halftone-light py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-[1fr_380px] gap-10 items-start">
          <Reveal>
            {done ? (
              <div data-testid="booking-success" className="cb-card p-10 text-center">
                <PartyPopper className="w-14 h-14 mx-auto text-cb-pink" />
                <h2 className="font-display font-bold uppercase text-2xl sm:text-3xl mt-4">Request Sent!</h2>
                <p className="mt-3 font-bold text-cb-black/60 text-base">
                  We just emailed the Crazy Buns crew and sent a confirmation to your inbox.
                  We'll get back to you within 24 hours. Life is better with a bun!
                </p>
                <button data-testid="booking-again-btn" onClick={() => { setDone(false); setForm(INITIAL); }} className="cb-btn cb-btn-teal mt-7">
                  Book Another Event
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="booking-form" className="cb-card p-7 sm:p-10 -rotate-1 hover:rotate-0">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="cb-label" htmlFor="bk-name">Your Name *</label>
                    <input id="bk-name" data-testid="booking-form-name" required className="cb-input" placeholder="Janey Rollins" value={form.name} onChange={set("name")} />
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-email">Email *</label>
                    <input id="bk-email" data-testid="booking-form-email" required type="email" className="cb-input" placeholder="you@email.com" value={form.email} onChange={set("email")} />
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-phone">Phone *</label>
                    <input id="bk-phone" data-testid="booking-form-phone" required className="cb-input" placeholder="(555) 123-4567" value={form.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-location">Event Location / City *</label>
                    <input id="bk-location" data-testid="booking-form-location" required className="cb-input" placeholder="Springfield Community Park" value={form.location} onChange={set("location")} />
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-date">Event Date *</label>
                    <input id="bk-date" data-testid="booking-form-date" required type="date" className="cb-input" value={form.event_date} onChange={set("event_date")} />
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-time">Start Time</label>
                    <input id="bk-time" data-testid="booking-form-time" type="time" className="cb-input" value={form.event_time} onChange={set("event_time")} />
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-type">Event Type *</label>
                    <select id="bk-type" data-testid="booking-form-event-type" className="cb-input" value={form.event_type} onChange={set("event_type")}>
                      {EVENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="cb-label" htmlFor="bk-menu">Menu Pick *</label>
                    <select id="bk-menu" data-testid="booking-form-menu-choice" className="cb-input" value={form.menu_choice} onChange={set("menu_choice")}>
                      {MENU_OPTIONS.map((o) => <option key={o.name} value={o.name}>{o.name} (${o.rate}/guest)</option>)}
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label className="cb-label" htmlFor="bk-guests">Estimated Guests: <span className="text-cb-pink">{form.guests}</span></label>
                  <input
                    id="bk-guests"
                    data-testid="booking-form-guest-count-slider"
                    type="range" min="10" max="500" step="5"
                    className="w-full accent-cb-pink cursor-pointer"
                    value={form.guests}
                    onChange={set("guests")}
                  />
                  <div className="flex justify-between text-xs font-extrabold text-cb-black/50"><span>10</span><span>500</span></div>
                </div>

                <div className="mt-6">
                  <label className="cb-label" htmlFor="bk-notes">Special Requests / Notes</label>
                  <textarea
                    id="bk-notes" data-testid="booking-form-notes" rows={4} className="cb-input resize-none"
                    placeholder="Allergies, timing quirks, surprise birthdays — tell us everything!"
                    value={form.notes} onChange={set("notes")}
                  />
                </div>

                <button
                  type="submit"
                  data-testid="booking-form-submit-button"
                  disabled={mutation.isPending}
                  className="cb-btn cb-btn-pink w-full mt-8 !py-4 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {mutation.isPending ? <Loader2 className="w-5 h-5 animate-spin" /> : <PartyPopper className="w-5 h-5" />}
                  {mutation.isPending ? "Sending..." : "Send Booking Request"}
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:sticky lg:top-24">
            <div data-testid="booking-estimate-card" className="cb-card bg-cb-black text-white p-8 rotate-1 hover:rotate-0">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-11 h-11 rounded-2xl border-3 border-cb-yellow bg-cb-yellow flex items-center justify-center">
                  <Calculator className="w-5 h-5 text-cb-black" />
                </div>
                <h3 className="font-display font-semibold uppercase text-lg">Instant Ballpark</h3>
              </div>
              <div className="text-center py-6 border-y-2 border-dashed border-white/20">
                <div className="font-display font-bold text-5xl text-cb-yellow" data-testid="booking-estimate-value">
                  ${estimate.toLocaleString()}
                </div>
                <div className="text-xs font-extrabold uppercase tracking-widest text-white/50 mt-2">estimated total</div>
              </div>
              <ul className="mt-5 space-y-2 text-sm font-bold text-white/70">
                <li className="flex justify-between"><span>Truck & crew base fee</span><span className="text-white">${BASE_FEE}</span></li>
                <li className="flex justify-between">
                  <span>{form.guests} guests × {form.menu_choice}</span>
                  <span className="text-white">${(estimate - BASE_FEE).toLocaleString()}</span>
                </li>
              </ul>
              <p className="mt-5 text-xs font-bold text-white/40">
                Ballpark only — final quote depends on date, distance, and menu. We confirm everything by email.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
